<template>
	<div>
		<div id="c-admin" class="form-builder cognito">
			<div id="c-forms-build" ref="buildContainer" class="form-builder__container c-app-container">
				<BuilderLayout :key="builderKey" />
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent, ref, computed, getCurrentInstance, onUnmounted, inject, provide } from 'vue';
	import type { PropType } from 'vue';
	import type { Route } from 'vue-router';
	import type { Dictionary } from 'vue-router/types/router';
	import type { LegacyCognitoConfig } from './LegacyCognitoConfig';
	import './library-scripts';
	import { serviceRequest } from '@cognitoforms/api/services/service-request';
	import { buildQueryString } from '@cognitoforms/utils/url-helper';
	import cognitoMessenger from 'src/utilities/cognito-messaging';
	import '@legacy/Scripts/component/Cognito-typeahead';
	import '@legacy/Scripts/component/Cognito-toggle';
	import '@legacy/Scripts/component/Cognito-timepicker';
	import '@legacy/Scripts/component/Cognito-datepicker';
	import '@legacy/Scripts/component/jquery.focusable';
	import '@legacy/Scripts/component/Sortable';
	import { CognitoConfiguration } from '@cognitoforms/utils/clientside-configuration';
	import { handlePageAccessError, handlePageDisposalError } from '../router';
	import type { LegacyPage } from './utils/utils';
	import { injectDynamicPageScript, unlinkStylesheet } from './utils/utils';
	import { loadLibraryScript, loadRequiredScripts, loadSharedScript } from './utils/legacy-script';
	import { appendLibraryExoWebTemplates, appendSharedExoWebTemplates } from './utils/legacy-template';
	import BuilderLayout from './builder/BuilderLayout.vue';
	import { disposeLegacyPageState, ensureCognitoGlobal } from './utils/globals-management';
	import { injectAdminFormApi, removeAdminFormApi } from './utils/admin-api';
	import { useAdminComponents } from './utils/admin-components';
	import initBuildPage from '@legacy/Views/Shared/build';
	import type { RecentPage } from '@cognitoforms/types/server-types/model/recent-page';
	import { PageType } from '@cognitoforms/types/server-types/model/recent-page+-page-type';
	import { globalStore } from 'src/stores/global-store';
	import { useRouter } from 'vue-router/composables';
	import { checkRole } from 'src/views/admin/utils/role-check';
	import notify from 'src/utilities/notify';
	import notifyWithButton from 'src/utilities/notify-with-button';
	import { ViewType } from '@cognitoforms/types/server-types/forms/model/view-type';
	import { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';
	import type { PageModel } from '@cognitoforms/types/server-types/services/controllers/svc-controllers/form-builder-controller+-page-model';
	import { useCurrentForm } from './utils/use-current-form';
	import { setDocumentTemplateDefaultValues } from 'src/utilities/document-templates';
	import type { SystemTemplateOptions } from '@cognitoforms/types/server-types/forms/model/system-template-options';
	import { websocketConnection } from 'src/utilities/websocket/websocket-connection-factory';
	import { initDiagram } from './builder-workflow-diagram';
	import { SaveFormEvent, useEventBus } from './utils/useEventBus';
	import { isToken } from 'src/components/form-controls/tokenUtils';
	import { expandFieldInfos } from './utils/field-info-conversion-utils';
	import { createFormStore, createTokenStore, FormBuilderService } from '@cognitoforms/form-builder';
	import type { FormStore, TokenStore } from '@cognitoforms/form-builder';
	import { builderAgentComposableKey, formBuilderServiceKey, builderStateKey, tokenStoreKey } from 'src/utilities/injection-keys';
	import { createLegacyMirroredBuilder, setupBindings, applySaveResponse, applyDefaultedEntrySummary, bindCollections } from 'src/utilities/legacy-form-adapter/legacy-mirrored-builder';
	import type { SaveFormResponse } from 'src/utilities/legacy-form-adapter/legacy-mirrored-builder';
	import type { BuilderState } from 'src/composables/use-builder-state';
	import { resetBuilderState, useBuilderState } from 'src/composables/use-builder-state';
	import type { useBuilderAgent } from 'src/composables/use-builder-agent';

	// Local helper functions for easier understanding of navigation logic
	function getModel(to: Route): Promise<PageModel> {
		if (to.params && to.params.formUrlName)
			return serviceRequest(`svc/form-builder/model?formName=${to.params.formUrlName}`, null, { method: 'get' });
		else
			return serviceRequest('svc/form-builder/model', null, { method: 'get' });
	}

	function reloadFormStore(vm: { builderService: FormBuilderService, builderAgentUtil: ReturnType<typeof useBuilderAgent>, builderState: BuilderState, formStore: FormStore, tokenStore: TokenStore }, pageModel: PageModel) {
		const form = JSON.parse(pageModel.NewFormJson);
		const formStore = createFormStore(form);
		vm.formStore = formStore;

		vm.builderService.reinitialize(formStore);
		vm.formStore = formStore;
		const cognito: any = ensureCognitoGlobal();
		cognito.formStore = formStore;
		if (vm.builderAgentUtil.builderAgentEnabled.value)
			vm.builderAgentUtil.enableBuilderAgent(vm.builderService, formStore);
		resetBuilderState(vm.builderState);
		vm.tokenStore.$reset();
	}

	export default defineComponent({
		components: { BuilderLayout },
		async beforeRouteEnter(to, from, next) {
			if (to.params.sameForm) {
				return next((vm: Vue & LegacyPage) => {
					vm.$emit('component-loaded');
					vm.trackRecentPages();
				});
			}

			try {
				const pageModel = await getModel(to);
				to.params.model = pageModel as never;
				next(async (vm: Vue & LegacyPage) => {
					try {
						// If the user navigates rapidly to the page more than once, wait for the existing
						// navigation to complete to avoid errors due to concurrent page setup
						if (vm.initialized) {
							await vm.initialized;
							await vm.disposeLegacyPage();
						}

						if (from.name !== to.name)
							reloadFormStore(vm, pageModel);

						vm.initialized = vm.initializeLegacyPage();
						await vm.initialized;
						vm.trackRecentPages();
					}
					catch (error) {
						handlePageAccessError(error, to, from, next);
					}
				});
			}
			catch (error) {
				handlePageAccessError(error, to, from, next);
			}
		},
		async beforeRouteUpdate(to, from, next) {
			if (to.params.sameForm) {
				this.$nextTick(() => {
					this.$emit('component-loaded');
					this.trackRecentPages();
				});

				return next();
			}
			else {
				this.formMessage?.close();
				this.formMessage = null;
				this.isLeaving = true;
			}

			await this.initialized;
			try {
				try {
					// Dispose any page state related to the previous route (i.e. form)
					this.disposeLegacyPage();
				}
				catch (e) {
					handlePageDisposalError(e, to, from, next);
					return;
				}

				// Fetch the model for the new route and initialize the page
				const pageModel = await getModel(to);
				to.params.model = pageModel as never;
				next();

				reloadFormStore(this, pageModel);
				this.initialized = this.initializeLegacyPage();
				await this.initialized;
				this.trackRecentPages();
			}
			catch (error) {
				handlePageAccessError(error, to, from, next);
			}
		},
		async beforeRouteLeave(to, from, next) {
			if (to.params.sameForm)
				return next();

			await this.initialized;

			// Prompt for unusaved changes if needed
			const navigate = window['Cognito']?.navigate || (() => Promise.resolve());
			await navigate({ to, from });

			this.formMessage?.close();
			this.formMessage = null;
			this.isLeaving = true;

			try {
				// Additionally we need to clean up the stylesheet the preview dialog adds
				const linkElement = document.querySelector('link[href="/Content/public-form.css"]');
				if (linkElement)
					unlinkStylesheet('/Content/public-form.css');

				// Dispose any page state before leaving
				this.disposeLegacyPage();
			}
			catch (e) {
				handlePageDisposalError(e, to, from, next);
				return;
			}

			next();
		},
		inheritAttrs: false,
		props: {
			orgcode: { type: String, required: true },
			formUrlName: { type: String, default: null },
			model: { type: Object as PropType<PageModel>, required: true },
			// System Template Query Parameters
			templateName: { type: String, default: null },
			defaultName: { type: String, default: null },
			lookupSourceForm: { type: String, default: null },
			isGuestList: { type: Boolean, default: false },
			personFormType: { type: String, default: 'None' }
		},
		emits: ['component-loaded', 'open-role-settings'],
		setup(props, { emit }) {
			const newForm = JSON.parse(props.model.NewFormJson);
			const builderState = useBuilderState();
			const formStore = createFormStore(newForm);
			const tokenStore = createTokenStore();
			const builderService = createLegacyMirroredBuilder(new FormBuilderService(formStore));
			const builderAgentUtil = inject(builderAgentComposableKey)!;

			const cognito = ensureCognitoGlobal() as any;
			cognito.builderService = builderService;
			cognito.formStore = formStore;
			cognito.useBuilderAgent = () => {
				builderAgentUtil.enableBuilderAgent(builderService, formStore);
			};

			const router = useRouter();
			const trackRecentPages = () => {
				if (!globalStore.forms)
					return;

				const formId: string = props.formUrlName !== null && !isNaN(Number(props.formUrlName))
					? props.formUrlName
					: Object.values(globalStore.forms).find(form => form.UrlName === props.formUrlName)?.Id;
				if (!formId)
					return;

				const page: RecentPage = {
					Id: formId,
					Type: PageType.Build,
					ViewId: null
				};

				globalStore.trackRecentPage(page, router);
			};

			globalStore.isReady.then(async () => {
				// if a user navigates directly to a page, the call to trackRecentPages will return early since globalStore is not ready
				// once globalStore loads, track the page
				trackRecentPages();

				if (props.formUrlName !== null && !isNaN(Number(props.formUrlName))) {
					const urlName = Object.values(globalStore.forms).find(form => form.Id === props.formUrlName).UrlName;
					const preferredUrl = `/${globalStore.organization.Code}/${urlName}/build`;
					window.history.replaceState({}, null, preferredUrl);
				}
			});

			const createElement = getCurrentInstance().proxy.$createElement;

			// Handles special events; such as the form being deleted or losing access
			const { formMessage, isLeaving } = useCurrentForm(createElement);

			const onFormCreated = (
				formId: string,
				formInternalName: string,
				formUrlName: string,
				formName: string,
				formTitle: string,
				folderId: string
			) => {
				// Show toast notification if form is saved in folder
				if (folderId) {
					const folder = globalStore.folders[folderId];
					if (folder)
						notify(`${formName} saved to folder ${folder.Name}`, 'success');
				}

				const formInStore = Object.values(globalStore.forms).find(userFormMeta => userFormMeta.Id === formId);
				if (!formInStore && !globalStore.isInReadOnlyMode) {
					const viewNumber = `${globalStore.organization.Plan?.AllowEntryViews ? '1' : '0'}`;
					const viewId = `${formId}-${viewNumber}`;
					globalStore.addFormLocally({
						Id: formId,
						InternalName: formInternalName,
						Name: formName,
						Title: formTitle,
						FolderId: folderId,
						UrlName: formUrlName,
						EntryViews: [{
							Id: viewId,
							Name: 'All Entries',
							InternalName: `${viewNumber}-all-entries`,
							Type: ViewType.Table,
							RoleId: null,
							IsTaskView: false,
							SequenceKey: null,
							Token: null,
							Entries: 0,
							PreventEntryCreation: false,
							ShowPageBreaks: false,
							Valid: true,
							TaskDueDate: null,
							TaskPriority: null,
							IsUserSpecific: null,
							IsAssignedRole: null,
							AfterSubmitViewId: null,
							LastUpdated: null
						}]
					});
				}

				const { emit: emitSaveForm } = useEventBus(SaveFormEvent);
				emitSaveForm({ formId, formName });

				const entriesUrl = `/${props.orgcode}/${formUrlName}/entries`;
				window.history.replaceState(null, null, entriesUrl);

				const builderUrl = `/${props.orgcode}/${formUrlName}/build`;
				window.history.pushState(null, null, builderUrl);
			};

			let roleAssignmentsUpdatedSubscription;
			const subscribeToRoleAssignmentUpdates = (onRoleAssignmentsUpdated) => {
				if (roleAssignmentsUpdatedSubscription)
					roleAssignmentsUpdatedSubscription.unsubscribe();

				roleAssignmentsUpdatedSubscription = websocketConnection.subscribeToEvents('FormsProfile', (formIds) => {
					onRoleAssignmentsUpdated(formIds);
				});
			};

			const notifyPersonFieldRoleDefaulted = (fieldName: string, role: string, openRoleSettings: () => void) => {
				const messageElement = createElement('span', {
					domProps: { innerHTML: `The <i>${fieldName}</i> field has been assigned to the <i>${role}</i> role.` }
				});

				notifyWithButton(createElement, messageElement, 'Manage Roles', openRoleSettings, 'custom', 3000, true, 'public');
			};

			const notifyWorkflowLinkAuthenticationDefaulted = () => {
				notify('Workflow link authentication enabled', 'custom', 3000, true, 'workflow');
			};

			onUnmounted(() => {
				roleAssignmentsUpdatedSubscription.unsubscribe();
			});

			provide(formBuilderServiceKey, builderService);
			provide(builderStateKey, builderState);
			provide(tokenStoreKey, tokenStore);

			return {
				builderKey: ref(false),
				cleanupTasks: ref([]),
				initialized: ref(null),
				trackRecentPages,
				onFormCreated,
				onFormRenamed(formId: string, internalName: string, name: string, formUrlName: string) {
					if (globalStore.forms[formId])
						globalStore.updateFormLocally(formId, { InternalName: internalName, Name: name, UrlName: formUrlName ?? internalName });
				},
				formMessage,
				isLeaving,
				subscribeToRoleAssignmentUpdates,
				notifyPersonFieldRoleDefaulted,
				notifyWorkflowLinkAuthenticationDefaulted,
				builderService,
				builderAgentUtil,
				formStore,
				tokenStore,
				builderState: builderState
			};
		},
		methods: {
			async initializeLegacyPage() {
				// This key forces the BuilderLayout component to re-render.
				// Ensures that the legacy pages are properly initialized when navigating between forms.
				this.builderKey = !this.builderKey;

				// Cognito.config.baseUrl is set on the 'Cognito.config' object in the dynamic page /script.
				// However, it is needed by the tinyMCE init script, which evaluates before that script loads.
				const Cognito = ensureCognitoGlobal();
				const config: Partial<LegacyCognitoConfig> = (Cognito as any).config = (Cognito as any).config || {};
				config.baseUrl = CognitoConfiguration.SiteUrl;

				this.cleanupTasks.push(cognitoMessenger.addHandler('updateForm', data => {
					this.$router.replace({
						name: 'builder',
						params: {
							...this.$route.params,
							formUrlName: data.FormData.UrlName,
							sameForm: true,
							model: this.$route.params.model
						}
					} as Dictionary<unknown>);
				}));
				await Promise.all<unknown>([
					useAdminComponents(),
					injectAdminFormApi(this.model.ApiKey, this.$refs.buildContainer as HTMLElement),
					loadRequiredScripts().then(() =>
						// Additional scripts...
						Promise.all([
							loadLibraryScript('expression-validation'),
							loadLibraryScript('expression-builder'),
							loadLibraryScript('intellisense'),
							loadLibraryScript('fileupload'),
							loadSharedScript('entry-view'),
							loadSharedScript('form-validator'),
							loadSharedScript('payment-settings'),
							loadSharedScript('forms-documents'),
							loadSharedScript('SelectInput.cognito-select-input')
						]).then(() =>
							Promise.all([
								// Dynamic page script depends on library scripts
								injectDynamicPageScript('build', '/svc/form-builder/script' + buildQueryString({
									formName: this.formUrlName,
									templateName: this.templateName,
									templateOptions: JSON.stringify({
										FormName: this.defaultName,
										LookupSourceForm: this.lookupSourceForm,
										IsGuestList: this.isGuestList,
										PersonFormType: this.personFormType
									} as SystemTemplateOptions)
								})),

								// Loading ExoWeb view depends on shared scripts (i.e. Cognito.ready)
								appendSharedExoWebTemplates('forms-documents'),
								appendSharedExoWebTemplates('payment-settings'),
								appendSharedExoWebTemplates('SelectInput.cognito-select-input'),
								appendSharedExoWebTemplates('forms-folders-dialog'),
								appendLibraryExoWebTemplates('expression-builder'),
								appendLibraryExoWebTemplates('fileupload')
							])
						)
					)
				]);

				initBuildPage({
					emitFormCreated: this.onFormCreated,
					emitFormRenamed: this.onFormRenamed,
					getGlobalStoreFormMeta: (formId: string) => globalStore.allForms?.[formId],
					isOrgAdmin: computed(() => checkRole(ClientRole.Administrator)),
					templateName: this.templateName,
					subscribeToRoleAssignmentUpdates: this.subscribeToRoleAssignmentUpdates,
					setDocumentTemplateDefaultValues,
					initDiagram,
					notifyPersonFieldRoleDefaulted: this.notifyPersonFieldRoleDefaulted,
					notifyWorkflowLinkAuthenticationDefaulted: this.notifyWorkflowLinkAuthenticationDefaulted,
					isToken: isToken,
					expandFieldInfos: expandFieldInfos,
					onFormInitialized: () => {
						setupBindings(this.formStore, this.tokenStore);
						bindCollections(this.formStore);
					},
					onFormSaved: (data: SaveFormResponse) => { applySaveResponse(this.formStore, data); },
					onEntrySummaryDefaulted: (format: string | null) => { applyDefaultedEntrySummary(this.formStore, format); },
					builderState: this.builderState
				});
				this.$emit('component-loaded');
			},
			disposeLegacyPage() {
				for (const task of this.cleanupTasks)
					task();
				this.cleanupTasks = [];
				window['tinymce']?.remove();
				delete window['tinymce'];
				delete window['tinyMCE'];
				document.querySelector('script[data-namespace="tinyMCE"]')?.remove();
				// removing these here instead of in utils because they are defined properties in the js for this page
				delete window['Cognito']?.resources?.ratings;
				delete window['Cognito']?.resources?.toggleOptions;
				removeAdminFormApi();
				disposeLegacyPageState(this.$el as HTMLElement);
			}
		}
	});
</script>

<style lang="scss">
	@import '@legacy/Views/Shared/SelectInput/cognito-select-input';
	@import '@legacy/Views/Shared/forms-documents';
	@import '@legacy/Views/Shared/build';
	@import '@legacy/Views/Shared/payment-settings';
	@import '@legacy/Content/c-timepicker';
	@import '@legacy/Content/c-datepicker';
	@import '@legacy/Content/c-typeahead';
	@import '@legacy/Content/expression-builder';
	@import '@legacy/Content/fileupload';

	.admin--support-pinned {

		.form-builder {
			width: calc(100vw - var(--support-width));
			transition: width 0s ease-in-out .3s;

			&__container,
			:root &__scroll {
				width: calc(100vw - var(--support-width));
				transition: width 0s ease-in-out .3s;
			}
		}
	}

	.form-builder {
		position: relative;
		width: 100vw;
		background: linear-gradient(var(--shade-3), var(--shade-4));

		[data-color-scheme='dark'] & {
			background: linear-gradient(var(--shade-3), var(--shade-2));

			&__container {
				background-image: radial-gradient(var(--shade-4) 1px, transparent 0);
			}
		}

		&__container {
			position: relative;
			width: 100vw;
			background-image: radial-gradient(var(--shade-5) 1px, transparent 0);
			background-position: 10px 5px;
			background-size: 30px 30px;
		}

		:root &__scroll {
			width: 100vw;
			height: calc(100dvh - #{$app-nav__height + $secondary-bar-height});
		}

		:root [data-app-layout] &__scroll {
			height: calc(100dvh - $app-heading-height);
		}

		&__content {
			height: 100%;
		}

		.is-navigating & .c-forms-main .c-forms-settings *,
		.is-navigating & .c-forms-main [id='c-forms-layout'],
		.is-navigating & .c-forms-actionBar * {
			display: none;
		}
	}
</style>
